import * as React from 'react';
import { useState, useEffect, useContext, useCallback } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Table,
  Label,
  ModalFooter,
  Alert,
} from 'reactstrap';
import * as firebaseService from '../../services/firebase';
import { logger } from '../../logging';
import moment from 'moment';
import { useObjectVal } from 'react-firebase-hooks/database';
import { Loader } from '../../components/Loader';
import {
  getFunctions,
  httpsCallable,
  HttpsCallableResult,
} from 'firebase/functions';
import {
  equalTo,
  orderByChild,
  query,
  get,
  ref as fbRef,
  getDatabase,
  set,
} from 'firebase/database';
import { ref } from '../../utils/firebase';
import { useFeature } from 'flagged';
import { Link } from 'react-router-dom';
import * as R from 'ramda';

import { AddressbookMetadata } from '../../../functions/src/services/infra/dotdigital';
import { Addressbook } from '../account/dotdigital/AddressbooksTab';
import { checkOptinExists } from './Addressbooks';
import { defaultCategoryValues } from '../optins/EmailCategoryRow';
import { BrandInput } from '../../components/brand/BrandInput';
import { BrandService } from '../../components/brand/BrandLookup';
import { AbilityContext } from '../../auth/Can';

const addressbookStyles = require('./addressbook.css');

const DEFAULT_CATEGORY = 'main_artist';

interface AddressbookDuplicate {
  key: string;
  name: string;
  accountName: string;
}

interface ValidationResult {
  valid: boolean;
  duplicates?: AddressbookDuplicate[];
  errorMessage?: string;
}

export const AddressbookCreateModal = ({
  account,
  setIsCreateAddressbookOpen,
  isCreateAddressbookOpen,
  setIsCreateAddressbookConfirmationOpen,
  setNewAddressbook,
}) => {
  // --- Firebase & Permissions. ---
  const ifbApp = firebaseService.getIFBApp();
  const ifbDatabase = getDatabase(ifbApp);
  const damApp = firebaseService.getDAMApp();
  const functions = getFunctions(damApp);
  const ability = useContext(AbilityContext);
  const canLPR = ability.can('manage', 'lpr');
  const canAdminCountry = ability.can('manage', 'addressbook_country');

  // --- Form State ---
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [country, setCountry] = useState(
    account.territoryOperatingCountry.toUpperCase()
  );
  const [partyId, setPartyId] = useState<number | null>(
    (account.party_id &&
      /^\d+$/.test(account.party_id) &&
      parseInt(account.party_id, 10)) ||
      null
  );
  const [partyName, setPartyName] = useState<string | null>(
    account.party_name || null
  );
  const [partyIdValid, setPartyIdValid] = useState(
    !!(account.party_id && account.party_name)
  );

  const [canopusId, setCanopusId] = useState<string | null>(null);

  // --- Opt-in Category Derived State ---
  const [dataCapture, setDataCapture] = useState(false);
  const [excludeFromReporting, setExcludeFromReporting] = useState(false);
  const [nonConsented, setNonConsented] = useState(false);
  const [doNotShare, setDoNotShare] = useState(false);
  const [allowMerchRecommendations, setAllowMerchRecommendations] =
    useState(false);

  // --- Community Leader State ---
  const [communityLeaderId, setCommunityLeaderId] = useState<string | null>(
    null
  );
  const [availableLeaders, setAvailableLeaders] = useState({});
  const [availableLeadersSorted, setAvailableLeadersSorted] = useState<any[]>(
    []
  );

  // --- UI & Validation State ---
  const [saveIsDisabled, setSaveIsDisabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [categoryErrorMessage, setCategoryErrorMessage] = useState('');
  const [isCategoryValid, setIsCategoryValid] = useState<boolean | undefined>(
    undefined
  );
  const [categoryDuplicates, setCategoryDuplicates] = useState<any[]>([]);

  // --- Category-derived settings ---
  const [partyIdRequired, setPartyIdRequired] = useState(false);
  const [dataCaptureEditable, setDataCaptureEditable] = useState(false);
  const [excludeFromReportingEditable, setExcludeFromReportingEditable] =
    useState(false);
  const [nonConsentedEditable, setNonConsentedEditable] = useState(false);
  const [doNotShareEditable, setDoNotShareEditable] = useState(false);

  // --- Firebase Data Loading ---
  const [enumerations, loadingEnums, errorEnums] = useObjectVal(
    ref('enumerations')
  ) as [any, boolean, any];
  const [categoryValues, setCategoryValues] = useState<any>(null);
  const [loadingCategoryValues, setLoadingCategoryValues] = useState(true);
  const [errorCategoryValues, setErrorCategoryValues] = useState<any>(null);

  useEffect(() => {
    let isMounted = true;
    setLoadingCategoryValues(true);
    setErrorCategoryValues(null);
    const fetchCategoryValues = async () => {
      try {
        const snapshot = await get(ref(`optin_categories/email/${category}`));
        if (isMounted) setCategoryValues(snapshot.val());
      } catch (e) {
        if (isMounted) setErrorCategoryValues(e);
      } finally {
        if (isMounted) setLoadingCategoryValues(false);
      }
    };
    fetchCategoryValues();
    return () => {
      isMounted = false;
    };
  }, [category]);

  const [countries, loadingCountries, errorCountries] = useObjectVal(
    ref('countries')
  ) as [any, boolean, any];

  // --- Helper Functions ---

  const resetForm = useCallback(() => {
    setName('');
    setTag('');
    setCategory(DEFAULT_CATEGORY);
    const accountCountry = account.territoryOperatingCountry.toUpperCase();
    setCountry(accountCountry);

    const initialPartyId =
      (account.party_id &&
        /^\d+$/.test(account.party_id) &&
        parseInt(account.party_id, 10)) ||
      null;
    setPartyId(initialPartyId);
    const initialPartyName = account.party_name || null;
    setPartyName(initialPartyName);
    setPartyIdValid(!!(initialPartyId && initialPartyName));
    setCanopusId(null);
    setAllowMerchRecommendations(false);
    setCommunityLeaderId(null);

    setSaveIsDisabled(true);
    setSaving(false);
    setErrorMessage('');
    setCategoryErrorMessage('');
    setIsCategoryValid(undefined);
    setCategoryDuplicates([]);
    setAvailableLeaders({});
    setAvailableLeadersSorted([]);
  }, [account]);

  const checkCategoryValid = useCallback(
    async (
      cat: string,
      currentCountry: string,
      currentPartyId: number | null
    ): Promise<ValidationResult | null> => {
      if (currentPartyId === null || isNaN(currentPartyId)) {
        setCategoryErrorMessage('');
        setCategoryDuplicates([]);
        return { valid: true }; // No partyId, no validation to perform
      }

      try {
        const callable = httpsCallable(
          functions,
          'accountManager-callableCheckAddressbookCategoryValid'
        );
        const result = (await callable({
          category: cat,
          account_level: account.ddAccountLevel,
          account_type: account.ddAccountType,
          dd_account_id: account.ddAccountId,
          country: currentCountry,
          party_id: currentPartyId,
          b2b_b2c: account.isB2COrB2B,
        })) as HttpsCallableResult<ValidationResult>;

        const data = result.data;
        if (data && data.valid === false) {
          setCategoryErrorMessage(
            data.errorMessage || 'Category is not valid.'
          );
          setCategoryDuplicates(data.duplicates || []);
        } else {
          setCategoryErrorMessage('');
          setCategoryDuplicates([]);
        }
        return data;
      } catch (e) {
        logger.error(`[checkCategoryValid] error: ${e}`);
        setCategoryErrorMessage(
          'An error occurred while validating the category.'
        );
        setCategoryDuplicates([]);
        return { valid: false };
      }
    },
    [functions, account]
  );

  // --- Effects ---

  // Effect to reset form when modal is opened/closed
  useEffect(() => {
    if (isCreateAddressbookOpen) {
      resetForm();
    }
  }, [isCreateAddressbookOpen, resetForm]);

  // Effect to update fields based on selected category
  useEffect(() => {
    if (loadingCategoryValues || errorCategoryValues || !categoryValues) {
      return;
    }

    const defaultValues = R.mapObjIndexed(
      (value) => R.mapObjIndexed((v) => v.default, value),
      defaultCategoryValues
    );
    const defaultedCategories = R.mergeRight(defaultValues, categoryValues);

    setPartyIdRequired(defaultedCategories['partyId']['required']);
    setDataCaptureEditable(defaultedCategories['dataCapture']['editable']);
    setExcludeFromReportingEditable(
      defaultedCategories['excludeFromReporting']['editable']
    );
    setNonConsentedEditable(defaultedCategories['nonConsented']['editable']);
    setDoNotShareEditable(defaultedCategories['doNotShare']['editable']);

    setDataCapture(defaultedCategories['dataCapture']['default_value']);
    setExcludeFromReporting(
      defaultedCategories['excludeFromReporting']['default_value']
    );
    setNonConsented(defaultedCategories['nonConsented']['default_value']);
    setDoNotShare(defaultedCategories['doNotShare']['default_value']);
  }, [categoryValues, loadingCategoryValues, errorCategoryValues]);

  // Effect to fetch community leaders when party ID becomes valid
  useEffect(() => {
    const getAvailableLeaders = async (pId: number) => {
      try {
        const leadersRef = ref('community/leaders');
        const leadersQuery = query(
          leadersRef,
          orderByChild('party_id'),
          equalTo(pId)
        );
        const leadersSnap = await get(leadersQuery);
        const leaders = {};
        leadersSnap.forEach((v) => {
          const leader = v.val();
          if (pId === leader.party_id) {
            leaders[leader.id] = leader;
          }
        });

        const sortedLeaders = R.sort(
          (a: any, b: any) => a.name.localeCompare(b.name),
          R.values(leaders)
        );

        setAvailableLeaders(leaders);
        setAvailableLeadersSorted(sortedLeaders);
      } catch (e) {
        logger.error(`[getAvailableLeaders]: ${e}`);
        setAvailableLeaders({});
        setAvailableLeadersSorted([]);
      }
    };

    if (partyIdValid && partyId) {
      getAvailableLeaders(partyId);
    } else {
      setAvailableLeaders({});
      setAvailableLeadersSorted([]);
      setCommunityLeaderId(null);
    }
  }, [partyId, partyIdValid]);

  // Main validation effect to control the save button
  useEffect(() => {
    if (name.length < 2) {
      setErrorMessage('Name must be at least 2 characters.');
      setSaveIsDisabled(true);
      return;
    }
    if (partyIdRequired && !partyIdValid) {
      setErrorMessage('A valid Party ID is required for this category.');
      setSaveIsDisabled(true);
      return;
    }
    if (!tag) {
      setErrorMessage('A tag is required.');
      setSaveIsDisabled(true);
      return;
    }
    if (isCategoryValid === false) {
      // The specific category error message is already set
      setErrorMessage(
        categoryErrorMessage || 'The selected category is not valid.'
      );
      setSaveIsDisabled(true);
      return;
    }

    setErrorMessage('');
    setSaveIsDisabled(false);
  }, [
    name,
    tag,
    partyIdValid,
    partyIdRequired,
    isCategoryValid,
    categoryErrorMessage,
  ]);

  // --- Event Handlers ---

  const handleClose = () => {
    setIsCreateAddressbookOpen(false);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    if (newName.length < 128) {
      setName(newName);
    }
  };

  const handleNameBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value && account.ddAccountName) {
      setTag(`${e.target.value}_${account.ddAccountName}`);
    }
  };

  const handleTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTag = e.target.value;
    if (newTag.length < 128) {
      setTag(newTag);
    }
  };

  const handlePartyIdChange = async (
    pId: string,
    pName: string,
    cId: string
  ) => {
    const valid = typeof pName === 'string' && pName.trim() !== '';
    const newPartyId = (pId && parseInt(pId, 10)) || null;

    setPartyId(newPartyId);
    setPartyName(pName);
    setPartyIdValid(valid);
    setCanopusId(cId || null);
    setCommunityLeaderId(null); // Reset leader when party changes

    const result = await checkCategoryValid(category, country, newPartyId);
    setIsCategoryValid(result?.valid);
  };

  const handleCountryChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    const result = await checkCategoryValid(category, newCountry, partyId);
    setIsCategoryValid(result?.valid);
  };

  const handleCategoryChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newCategory = e.target.value;
    setCategory(newCategory);
    setCanopusId(null); // Reset fields dependent on category logic
    setCommunityLeaderId(null);

    const result = await checkCategoryValid(newCategory, country, partyId);
    setIsCategoryValid(result?.valid);
  };

  const handleLeaderChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommunityLeaderId(e.target.value || null);
  };

  const saveAddressbook = async () => {
    setSaving(true);
    setSaveIsDisabled(true);

    // Final validation checks
    const categoryValidationResult = await checkCategoryValid(
      category,
      country,
      partyId
    );
    if (!categoryValidationResult?.valid) {
      setErrorMessage(categoryErrorMessage || 'Category validation failed.');
      setSaving(false);
      return;
    }

    const optinIndex = 'country_channel_category_brandId_tag';
    const optinIndexValue =
      `${country}_email_${category}_${partyId}_${tag}`.toLowerCase();
    if (await checkOptinExists(optinIndex, optinIndexValue)) {
      setErrorMessage(
        'Duplicate optin - An optin already exists for this country/channel/category/brand/tag combination.'
      );
      setSaving(false);
      setSaveIsDisabled(false);
      return;
    }

    let addressbookMetadata: AddressbookMetadata;
    try {
      const callable = httpsCallable(
        functions,
        'accountManager-callableCreateAddressbook'
      );
      const res: any = await callable({
        accountId: account.ddAccountId,
        addressbook: { name },
      });
      addressbookMetadata = res.data as AddressbookMetadata;

      if (typeof addressbookMetadata.id === 'undefined') {
        throw new Error('No ID returned from DD when creating addressbook.');
      }
    } catch (e) {
      logger.error(`[saveAddressbook] error posting to DD: ${e}`);
      setErrorMessage(e.message);
      setSaving(false);
      setSaveIsDisabled(false);
      return;
    }

    const now = moment().format('YYYY-MM-DDTHH:mm:ssZ');
    const key = `${account.ddAccountId}_${addressbookMetadata.id}`;
    const newAddressbook: any = {
      id: addressbookMetadata.id,
      key,
      name,
      tag,
      category,
      country,
      accountId: parseInt(account.ddAccountId, 10),
      accountName: account.ddAccountName,
      created: now,
      modified: now,
      dataCapture,
      excludeFromReporting,
      nonConsented,
      doNotShare,
      allowMerchRecommendations,
      party_id: partyId,
      party_name: partyName,
      canopusId: canopusId,
      communityLeaderId: communityLeaderId,
      communityLeaderName:
        (communityLeaderId && availableLeaders[communityLeaderId]?.name) ||
        null,
    };

    // Add composite keys for Firebase queries
    const isPartyIdValidStr = partyIdValid ? 'true' : 'false';
    const hasCanopus = canopusId ? 'true' : 'false';
    newAddressbook.hascanopus_processed_accountid_addressbookid = `${hasCanopus}_false_${key}`;
    newAddressbook.hascanopus_processed_addressbookid = `${hasCanopus}_false_${addressbookMetadata.id}`;
    newAddressbook.haspartyid_processed_accountid_addressbookid = `${isPartyIdValidStr}_false_${key}`;
    newAddressbook.haspartyid_processed_addressbookid = `${isPartyIdValidStr}_false_${addressbookMetadata.id}`;
    newAddressbook.haspartyid_senttobq_accountid_addressbookid = `${isPartyIdValidStr}_false_${key}`;
    newAddressbook.account_addressbookname = `${account.ddAccountId}_${R.trim(
      name
    ).toLowerCase()}`;
    newAddressbook.has_party_id_and_no_canopus_id = partyIdValid && !canopusId;

    try {
      const newAddressbookRef = fbRef(ifbDatabase, `addressbooks/${key}`);
      await set(newAddressbookRef, newAddressbook);
    } catch (e) {
      logger.error(`[saveAddressbook] error saving to IFB: ${e}`);
      setErrorMessage('There was a problem saving the addressbook in DAM.');
      setSaving(false);
      setSaveIsDisabled(false);
      // Here you might want to delete the already created DD addressbook
      return;
    }

    setNewAddressbook(newAddressbook);
    setIsCreateAddressbookConfirmationOpen(true);
    handleClose();
  };

  // --- Render Logic ---

  if (loadingEnums || loadingCountries) {
    return <Loader loading={true} />;
  }

  if (errorEnums || errorCountries) {
    return (
      <div>{`Error: ${errorEnums?.message || errorCountries?.message}`}</div>
    );
  }

  const countriesValues = (Object.values(countries) as any) || [];
  // testing to see if we can still keep country as 'touppercase' everywhere if we just uppercase it here
  const countryOptions = countriesValues.map((c) => (
    <option value={c.value.toUpperCase()} key={c.value.toUpperCase()}>
      {c.label}
    </option>
  ));

  const addressbookCategories =
    Object.values(enumerations.addressbookCategories) || [];
  const categoryOptions = addressbookCategories
    .filter(
      (cat: any) =>
        cat.value !== 'unknown_list' &&
        (cat.value !== 'registration_list' || canLPR)
    )
    .map((cat: any) => (
      <option value={cat.value} key={cat.value}>
        {cat.label}
      </option>
    ));

  const booleanOptions = [
    { value: 'true', label: 'True' },
    { value: 'false', label: 'False' },
  ].map((opt) => (
    <option value={opt.value} key={opt.value}>
      {opt.label}
    </option>
  ));

  const leaderOptions = availableLeadersSorted.map((leader) => (
    <option value={leader.id} key={leader.id}>
      {leader.name}
    </option>
  ));

  const categoryDuplicatesRows = categoryDuplicates.map((duplicate, idx) => (
    <Row key={idx}>
      <Col sm={2}></Col>
      <Col sm={8}>
        <Alert
          color="warning"
          className={addressbookStyles.categoryDuplicatesAlert}
        >
          <Link
            to={`../../addressbooks?q=id-${
              duplicate.key && duplicate.key.split('_')[1]
            }-number`}
            target="_blank"
          >{`addressbook key: ${duplicate.key} | account name: ${duplicate.accountName} | addressbook name: ${duplicate.name}`}</Link>
        </Alert>
      </Col>
      <Col sm={2}></Col>
    </Row>
  ));

  const isLoading =
    saving || loadingEnums || loadingCountries || loadingCategoryValues;

  return (
    <Modal
      isOpen={isCreateAddressbookOpen}
      toggle={handleClose}
      className={addressbookStyles.createAddressbookModal}
    >
      <ModalHeader
        toggle={handleClose}
        className={addressbookStyles.createAddressbookModalHeader}
      >
        {`Create new Addressbook for DD Account: ${
          account.ddAccountName
        } (${account.territoryOperatingCountry.toUpperCase()})`}
      </ModalHeader>
      <ModalBody
        className={
          isLoading
            ? addressbookStyles.createAddressbookModalBodyDisabled
            : addressbookStyles.createAddressbookModalBody
        }
      >
        {errorMessage && (
          <Row>
            <Col sm={{ offset: 2, size: 8 }}>
              <Alert color="danger">{errorMessage}</Alert>
            </Col>
          </Row>
        )}
        {categoryErrorMessage && !errorMessage && (
          <Row>
            <Col sm={{ offset: 2, size: 8 }}>
              <Alert color="danger">{categoryErrorMessage}</Alert>
            </Col>
          </Row>
        )}
        {categoryDuplicates.length > 0 && (
          <React.Fragment>
            <Row className={addressbookStyles.categoryDuplicatesHeaderRow}>
              <Col sm={{ offset: 2, size: 8 }}>
                Category rule violation - duplicates:
              </Col>
            </Row>
            {categoryDuplicatesRows}
          </React.Fragment>
        )}

        {/* Form Fields */}
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Addressbook Country:</Label>
            <Input
              type="select"
              value={country}
              onChange={handleCountryChange}
              disabled={!canAdminCountry || isLoading}
            >
              {countryOptions}
            </Input>
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Optin Category:</Label>
            <Input
              type="select"
              value={category}
              onChange={handleCategoryChange}
              disabled={isLoading}
            >
              {categoryOptions}
            </Input>
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Addressbook Name:</Label>
            <Input
              type="text"
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              disabled={isLoading}
            />
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Tag:</Label>
            <Input
              type="text"
              value={tag}
              onChange={handleTagChange}
              disabled={isLoading}
            />
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <BrandInput
              name="party_id"
              value={partyId || ''}
              label="Party ID"
              brandFieldName="party_name"
              brandFieldNameLabel="Party Name"
              disabled={isLoading}
              handleOnChange={handlePartyIdChange}
              brandService={BrandService.PARTY_SERVICE}
            />
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Data Capture Enabled:</Label>
            <Input
              type="select"
              value={String(dataCapture)}
              onChange={(e) => setDataCapture(e.target.value === 'true')}
              disabled={!partyIdValid || !dataCaptureEditable || isLoading}
            >
              {booleanOptions}
            </Input>
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Exclude from Reporting:</Label>
            <Input
              type="select"
              value={String(excludeFromReporting)}
              onChange={(e) =>
                setExcludeFromReporting(e.target.value === 'true')
              }
              disabled={!excludeFromReportingEditable || isLoading}
            >
              {booleanOptions}
            </Input>
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Non Consented:</Label>
            <Input
              type="select"
              value={String(nonConsented)}
              onChange={(e) => setNonConsented(e.target.value === 'true')}
              disabled={!nonConsentedEditable || isLoading}
            >
              {booleanOptions}
            </Input>
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Do Not Share:</Label>
            <Input
              type="select"
              value={String(doNotShare)}
              onChange={(e) => setDoNotShare(e.target.value === 'true')}
              disabled={!doNotShareEditable || isLoading}
            >
              {booleanOptions}
            </Input>
          </Col>
        </Row>
        <Row className={addressbookStyles.createAddressbookModalBodyRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Label>Allow Merch Recommendations:</Label>
            <Input
              type="select"
              value={String(allowMerchRecommendations)}
              onChange={(e) =>
                setAllowMerchRecommendations(e.target.value === 'true')
              }
              disabled={
                !account.allowMerchRecommendationsForAddressbooks || isLoading
              }
            >
              {booleanOptions}
            </Input>
          </Col>
        </Row>

        {/* Community Leader Mapping */}
        {partyIdValid &&
          (availableLeadersSorted.length === 0 ? (
            <Row className={addressbookStyles.createAddressbookModalBodyRow}>
              <Col sm={{ offset: 2, size: 8 }}>
                <Label>
                  Community Leader Mapping: No leaders available for this Party
                  ID.
                </Label>
              </Col>
            </Row>
          ) : (
            <Row className={addressbookStyles.createAddressbookModalBodyRow}>
              <Col sm={{ offset: 2, size: 8 }}>
                <Label>Community Leader Mapping:</Label>
                <Table>
                  <thead>
                    <tr>
                      <th>Leader Name</th>
                      <th>Leader ID</th>
                      <th>Leader Party ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Input
                          type="select"
                          value={communityLeaderId || ''}
                          onChange={handleLeaderChanged}
                          disabled={isLoading}
                        >
                          <option value="">Please select</option>
                          {leaderOptions}
                        </Input>
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={communityLeaderId || ''}
                          disabled
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={
                            (communityLeaderId &&
                              availableLeaders[communityLeaderId]?.party_id) ||
                            ''
                          }
                          disabled
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>
          ))}
      </ModalBody>
      <ModalFooter className={addressbookStyles.createAddressbookModalFooter}>
        {isLoading && <Loader loading={true} />}
        <Row className={addressbookStyles.createAddressbookModalFooterRow}>
          <Col sm={{ offset: 2, size: 8 }}>
            <Button
              onClick={saveAddressbook}
              disabled={saveIsDisabled || isLoading}
              className={addressbookStyles.saveButton}
            >
              Save
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
